import React, { useState, useEffect } from 'react'
import { 
  BiSearch, 
  BiFilterAlt, 
  BiTrash, 
  BiStar, 
  BiDotsVerticalRounded,
  BiDownload,
  BiEnvelope,
  BiEnvelopeOpen,
  BiCheckCircle,
  BiX,
  BiArrowBack,
  BiReply,
  BiLink,
  BiTime,
  BiFile,
  BiUser,
  BiChevronDown,
  BiRefresh
} from 'react-icons/bi'
import './FormEmails.css'
import { buildApiUrl } from '../../config/api';

function FormEmails({ theme, projects }) {
  // State for projects with form submissions
  const [projectsWithForms, setProjectsWithForms] = useState([]);
  const [allEmails, setAllEmails] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [detailsView, setDetailsView] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [emails, setEmails] = useState([]);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Process projects and form submissions on mount
  useEffect(() => {
    if (!projects) return;

    // Create project options including "All Projects", now including all projects
    const allProjectsOption = {
      siteId: 'all',
      name: 'All Projects',
      formSubmissions: []
    };

    // Include all projects in the dropdown list
    setProjectsWithForms([allProjectsOption, ...projects]);
    setSelectedProject('all'); // Default to "All Projects"

    // Process form submissions from projects that have them
    const projectsWithSubmissions = projects.filter(project => 
      project.formSubmissions && project.formSubmissions.length > 0
    );

    // Process all form submissions into email format
    const processedEmails = projectsWithSubmissions.flatMap(project => 
      project.formSubmissions.map(submission => ({
        id: submission._id,
        projectId: project.siteId,
        projectName: project.name,
        form: "Contact Form",
        sender: submission.formData.email || 'No email provided',
        subject: `Form submission from ${submission.formData.name || 'Unknown'}`,
        dateObj: new Date(submission.submittedAt),
        date: new Date(submission.submittedAt).toLocaleString(),
        status: submission.isRead ? 'read' : 'unread',
        starred: false,
        formData: submission.formData
      }))
    ).sort((a, b) => b.dateObj - a.dateObj);

    setAllEmails(processedEmails);
  }, [projects]);

  // Get the selected project name
  const getSelectedProjectName = () => {
    const project = projectsWithForms.find(p => p.siteId === selectedProject);
    return project ? project.name : 'All Projects';
  };

  // Filter emails based on the selected project
  useEffect(() => {
    if (selectedProject === 'all') {
      setEmails(allEmails);
    } else {
      setEmails(allEmails.filter(email => email.projectId === selectedProject));
    }
    // Clear any selections when changing projects
    setSelectedEmails([]);
  }, [selectedProject, allEmails]);

  // Get the selected email object
  const selectedEmail = emails.find(email => email.id === selectedEmailId);

  // Filter emails based on search and active filter
  const filteredEmails = emails.filter(email => {
    // Apply search filter
    const matchesSearch = 
      email.sender.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (email.formData.name && email.formData.name.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Apply status filter
    if (activeFilter === 'all') return matchesSearch;
    if (activeFilter === 'unread') return email.status === 'unread' && matchesSearch;
    if (activeFilter === 'starred') return email.starred && matchesSearch;
    
    return matchesSearch;
  }).sort((a, b) => b.dateObj - a.dateObj); // Ensure consistent sorting

  // Toggle star status of an email
  const toggleStar = (e, id) => {
    e.stopPropagation(); // Prevent opening the email
    setAllEmails(allEmails.map(email => 
      email.id === id ? {...email, starred: !email.starred} : email
    ));
  };

  // Toggle read/unread status
  const toggleReadStatus = async (e, id) => {
    e.stopPropagation(); // Prevent opening the email
    
    const email = allEmails.find(email => email.id === id);
    console.log('Toggle email:', email);
    if (!email) return;

    const newIsRead = email.status === 'unread'; // If currently unread, we're marking as read

    try {
      const response = await fetch(buildApiUrl(`api/sites/${email.projectId}/form-submissions/${id}/read-status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isRead: newIsRead
        })
      });

      if (response.ok) {
        console.log('Toggle response:', response);
        // Update local state
        setAllEmails(allEmails.map(email => 
          email.id === id ? {
            ...email,
            status: newIsRead ? 'read' : 'unread'
          } : email
        ));
      } else {
        console.error('Failed to update read status');
      }
    } catch (error) {
      console.error('Error updating read status:', error);
    }
  };

  // Select or deselect an email
  const toggleSelectEmail = (e, id) => {
    e.stopPropagation(); // Prevent opening the email
    if (selectedEmails.includes(id)) {
      setSelectedEmails(selectedEmails.filter(emailId => emailId !== id));
    } else {
      setSelectedEmails([...selectedEmails, id]);
    }
  };

  // Select or deselect all visible emails
  const toggleSelectAll = () => {
    if (selectedEmails.length === filteredEmails.length && filteredEmails.length > 0) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(filteredEmails.map(email => email.id));
    }
  };

  // Update handleDeleteEmail for single delete
  const handleDeleteEmail = (e, id) => {
    e.stopPropagation(); // Prevent opening the email
    setEmailToDelete(id);
    setIsBulkDelete(false);
    setShowDeleteConfirmation(true);
  };

  // Update deleteSelected for bulk delete
  const handleBulkDelete = () => {
    setIsBulkDelete(true);
    setShowDeleteConfirmation(true);
  };

  // Unified confirm delete function
  const confirmDelete = async () => {
    try {
      if (isBulkDelete) {
        // Handle bulk delete
        const submissionsByProject = {};
        selectedEmails.forEach(emailId => {
          const email = allEmails.find(e => e.id === emailId);
          if (email) {
            if (!submissionsByProject[email.projectId]) {
              submissionsByProject[email.projectId] = [];
            }
            submissionsByProject[email.projectId].push(emailId);
          }
        });

        // Delete submissions for each project
        for (const [projectId, submissionIds] of Object.entries(submissionsByProject)) {
          const response = await fetch(buildApiUrl(`api/sites/${projectId}/form-submissions`), {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ submissionIds })
          });

          if (!response.ok) {
            throw new Error(`Failed to delete submissions for project ${projectId}`);
          }
        }

        // Update states after successful bulk delete
        const updatedAllEmails = allEmails.filter(email => !selectedEmails.includes(email.id));
        setAllEmails(updatedAllEmails);
        setEmails(updatedAllEmails.filter(email => selectedProject === 'all' || email.projectId === selectedProject));
        setSelectedEmails([]);

        // Close detail view if deleted email was being viewed
        if (selectedEmailId && selectedEmails.includes(selectedEmailId)) {
          setDetailsView(false);
          setSelectedEmailId(null);
        }
      } else {
        // Handle single delete
        const email = allEmails.find(email => email.id === emailToDelete);
        if (!email) return;

        const response = await fetch(buildApiUrl(`api/sites/${email.projectId}/form-submissions/${emailToDelete}`), {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const updatedAllEmails = allEmails.filter(email => email.id !== emailToDelete);
          setAllEmails(updatedAllEmails);
          setEmails(updatedAllEmails.filter(email => selectedProject === 'all' || email.projectId === selectedProject));
          
          if (selectedEmails.includes(emailToDelete)) {
            setSelectedEmails(selectedEmails.filter(emailId => emailId !== emailToDelete));
          }
          
          if (selectedEmailId === emailToDelete) {
            setDetailsView(false);
            setSelectedEmailId(null);
          }
        } else {
          throw new Error('Failed to delete submission');
        }
      }
    } catch (error) {
      console.error('Error deleting submission(s):', error);
      alert('Failed to delete submission(s). Please try again.');
    } finally {
      setShowDeleteConfirmation(false);
      setEmailToDelete(null);
      setIsBulkDelete(false);
    }
  };

  // Open email details view
  const openEmailDetails = async (id) => {
    const email = allEmails.find(email => email.id === id);
    if (!email) return;

    if (email.status === 'unread') {
      try {
        const response = await fetch(buildApiUrl(`api/sites/${email.projectId}/form-submissions/${id}/read-status`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            isRead: true // Always mark as read when opening
          })
        });

        if (response.ok) {
          // Update local state
          setAllEmails(allEmails.map(email => 
            email.id === id ? {
              ...email,
              status: 'read'
            } : email
          ));
        }
      } catch (error) {
        console.error('Error updating read status:', error);
      }
    }
    
    setSelectedEmailId(id);
    setDetailsView(true);
  };

  // Close email details view
  const closeEmailDetails = () => {
    setDetailsView(false);
    setSelectedEmailId(null);
  };

  // Toggle project dropdown
  const toggleProjectDropdown = () => {
    setIsProjectDropdownOpen(!isProjectDropdownOpen);
  };

  // Select a project
  const selectProject = (projectId) => {
    setSelectedProject(projectId);
    setIsProjectDropdownOpen(false);
  };

  // Render form data based on the type of value
  const renderFormValue = (value) => {
    if (Array.isArray(value)) {
      return (
        <ul className="form-value-list">
          {value.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      );
    } else if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    } else {
      return value;
    }
  };

  // Add refreshEmails function
  const refreshEmails = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      const user = localStorage.getItem('user');
      if (!user) {
        throw new Error('User not found');
      }
      const userData = JSON.parse(user);

      const response = await fetch(buildApiUrl(`api/sites/user/${userData.id}`));
      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }
      const data = await response.json();
      
      // Include all projects in dropdown
      const allProjectsOption = {
        siteId: 'all',
        name: 'All Projects',
        formSubmissions: []
      };
      setProjectsWithForms([allProjectsOption, ...(data.sites || [])]);

      // Process submissions only from projects that have them
      const projectsWithSubmissions = (data.sites || []).filter(project => 
        project.formSubmissions && project.formSubmissions.length > 0
      );

      const processedEmails = projectsWithSubmissions.flatMap(project => 
        project.formSubmissions.map(submission => ({
          id: submission._id,
          projectId: project.siteId,
          projectName: project.name,
          form: "Contact Form",
          sender: submission.formData.email || 'No email provided',
          subject: `Form submission from ${submission.formData.name || 'Unknown'}`,
          dateObj: new Date(submission.submittedAt),
          date: new Date(submission.submittedAt).toLocaleString(),
          status: submission.isRead ? 'read' : 'unread',
          starred: false,
          formData: submission.formData
        }))
      ).sort((a, b) => b.dateObj - a.dateObj);

      setAllEmails(processedEmails);
      setEmails(processedEmails.filter(email => 
        selectedProject === 'all' || email.projectId === selectedProject
      ));
      
      setSelectedEmails([]);

    } catch (error) {
      console.error('Error refreshing emails:', error);
      alert('Failed to refresh emails. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="form-emails-container" data-theme={theme}>
      <div className="workspace-tabs">
        {/* Project Selection Dropdown */}
        <div className="project-selector-container">
          <div className="project-selector-label">Project:</div>
          <div className="project-selector">
            <div 
              className="project-selector-dropdown" 
              onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
            >
              <span>{getSelectedProjectName()}</span>
              <BiChevronDown />
            </div>
            
            {isProjectDropdownOpen && (
              <div className="project-dropdown-menu">
                {projectsWithForms.map(project => {
                  // Count submissions for this project
                  const submissionCount = project.siteId === 'all' 
                    ? allEmails.length 
                    : allEmails.filter(email => email.projectId === project.siteId).length;
                  
                  return (
                    <div 
                      key={project.siteId} 
                      className={`project-dropdown-item ${selectedProject === project.siteId ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedProject(project.siteId);
                        setIsProjectDropdownOpen(false);
                      }}
                    >
                      <span className="project-name">{project.name}</span>
                      <span className="submission-count">
                        {submissionCount > 0 ? `(${submissionCount})` : '(0)'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        
        <div className="workspace-metrics">
          <div className="workspace-metric">
            <span className="metric-label">Total Submissions</span>
            <span className="metric-value-small">{emails.length}</span>
          </div>
          <div className="workspace-metric">
            <span className="metric-label">Unread</span>
            <span className="metric-value-small">
              {emails.filter(e => e.status === 'unread').length}
            </span>
          </div>
        </div>
      </div>

      {!detailsView ? (
        // Email list view
        <>
          <div className="emails-toolbar">
            <div className="email-toolbar-left">
              <div className="email-bulk-actions">
                <input 
                  type="checkbox" 
                  className="select-all-checkbox"
                  checked={selectedEmails.length === filteredEmails.length && filteredEmails.length > 0}
                  onChange={toggleSelectAll}
                />
                
                {selectedEmails.length > 0 && (
                  <>
                    <button className="toolbar-btn delete-btn" onClick={handleBulkDelete}>
                      <BiTrash />
                    </button>
                    <button className="toolbar-btn">
                      <BiDownload />
                    </button>
                    <button className="toolbar-btn">
                      <BiCheckCircle />
                    </button>
                  </>
                )}
              </div>
              
              <div className="email-filters">
                <button 
                  className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('all')}
                >
                  All
                </button>
                <button 
                  className={`filter-btn ${activeFilter === 'unread' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('unread')}
                >
                  Unread
                </button>
                <button 
                  className={`filter-btn ${activeFilter === 'starred' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('starred')}
                >
                  Starred
                </button>
              </div>
            </div>
            
            <div className="email-toolbar-right">
              <button 
                className={`toolbar-btn refresh-btn ${isRefreshing ? 'refreshing' : ''}`}
                onClick={refreshEmails}
                disabled={isRefreshing}
              >
                <BiRefresh className={isRefreshing ? 'spin' : ''} />
              </button>
              <div className="email-search">
                <BiSearch className="search-icon" />
                <input 
                  type="text"
                  placeholder="Search emails..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="email-search-input"
                />
              </div>
              <button className="toolbar-btn">
                <BiFilterAlt />
              </button>
            </div>
          </div>

          <div className="emails-list">
            {filteredEmails.length > 0 ? (
              filteredEmails.map(email => (
                <div 
                  key={email.id} 
                  className={`email-item ${email.status === 'unread' ? 'unread' : ''}`}
                  onClick={() => openEmailDetails(email.id)}
                >
                  <div className="email-select" onClick={(e) => e.stopPropagation()}>
                    <input 
                      type="checkbox"
                      checked={selectedEmails.includes(email.id)}
                      onChange={(e) => toggleSelectEmail(e, email.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  
                  <div className="email-star" onClick={(e) => toggleStar(e, email.id)}>
                    <BiStar className={email.starred ? 'starred' : ''} />
                  </div>
                  
                  <div className="email-status" onClick={(e) => toggleReadStatus(e, email.id)}>
                    {email.status === 'unread' ? 
                      <BiEnvelope className="email-icon unread-icon" /> : 
                      <BiEnvelopeOpen className="email-icon read-icon" />
                    }
                  </div>
                  
                  <div className="email-project-tag">
                    {email.projectName}
                  </div>
                  
                  <div className="email-form">{email.form}</div>
                  
                  <div className="email-sender">{email.sender}</div>
                  
                  <div className="email-subject">
                    <span className="subject-text">{email.subject}</span>
                  </div>
                  
                  <div className="email-date">{email.date}</div>
                </div>
              ))
            ) : (
              <div className="no-emails">
                <div className="no-data-message">
                  <BiEnvelope className="no-data-icon" />
                  <h3>No emails found</h3>
                  <p>No emails match your current filter criteria</p>
                </div>
              </div>
            )}
          </div>

          <div className="email-pagination">
            <div className="pagination-info">
              Showing 1-{filteredEmails.length} of {filteredEmails.length} emails
            </div>
            <div className="pagination-controls">
              <button className="pagination-btn" disabled>Previous</button>
              <button className="pagination-btn active">1</button>
              <button className="pagination-btn" disabled>Next</button>
            </div>
          </div>
        </>
      ) : (
        // Email details view
        <div className="email-details-container">
          <div className="email-details-header">
            <button className="back-button" onClick={closeEmailDetails}>
              <BiArrowBack />
              <span>Back to emails</span>
            </button>
            
            <div className="email-details-actions">
              <button className="toolbar-btn">
                <BiReply />
              </button>
              <button className="toolbar-btn">
                <BiDownload />
              </button>
              <button className="toolbar-btn delete-btn" onClick={(e) => handleDeleteEmail(e, selectedEmailId)}>
                <BiTrash />
              </button>
            </div>
          </div>
          
          <div className="email-details-content">
            <div className="email-details-meta">
              <h2 className="email-details-subject">{selectedEmail.subject}</h2>
              
              <div className="project-badge">
                {selectedEmail.projectName}
              </div>
              
              <div className="meta-info">
                <div className="meta-row">
                  <div className="meta-label"><BiUser /> From:</div>
                  <div className="meta-value">
                    {selectedEmail.formData.name} <span className="meta-email">&lt;{selectedEmail.sender}&gt;</span>
                  </div>
                </div>
                
                <div className="meta-row">
                  <div className="meta-label"><BiFile /> Form:</div>
                  <div className="meta-value">{selectedEmail.form}</div>
                </div>
                
                <div className="meta-row">
                  <div className="meta-label"><BiTime /> Date:</div>
                  <div className="meta-value">{selectedEmail.date}</div>
                </div>
              </div>
            </div>
            
            <div className="form-data-container">
              <h3 className="form-data-title">Form Submission Data</h3>
              
              <div className="form-data-grid">
                {Object.entries(selectedEmail.formData).map(([key, value]) => (
                  <div className="form-data-row" key={key}>
                    <div className="form-field-name">
                      {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}:
                    </div>
                    <div className="form-field-value">
                      {renderFormValue(value)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update the confirmation modal */}
      {showDeleteConfirmation && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirmation(false)}>
          <div className="delete-confirmation-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <button 
                className="close-modal"
                onClick={() => setShowDeleteConfirmation(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-content">
              {isBulkDelete ? (
                <>
                  <p>Are you sure you want to delete {selectedEmails.length} selected email{selectedEmails.length > 1 ? 's' : ''}?</p>
                  <p className="warning-text">This action cannot be undone and will delete all selected emails.</p>
                </>
              ) : (
                <>
                  <p>Are you sure you want to delete this email submission?</p>
                  <p className="warning-text">This action cannot be undone.</p>
                </>
              )}
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setEmailToDelete(null);
                  setIsBulkDelete(false);
                }}
              >
                Cancel
              </button>
              <button 
                className="delete-btn"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FormEmails 